import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useSettings } from "../../lib/useSettings";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  CheckCircle2, ShieldCheck, Award, Briefcase, Gem, Save, Loader2, Info, Users, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

export default function BusinessRulesAdmin({ section = "all" }) {
  const { settings, refresh } = useSettings();
  const [form, setForm] = useState({
    order_approval_mode: "auto",
    order_approval_notify_emails: [],
    b2b_enabled: false,
    b2b_price_display: "TTC",
    b2b_allow_wire_transfer: true,
    b2b_wire_terms_days: 30,
    b2b_default_discount_pct: 0,
    b2b_pro_tiers: [],
    loyalty_enabled: false,
    loyalty_cashback_pct: 5,
    loyalty_cashback_delay_days: 15,
    loyalty_referral_bonus_cents: 1000,
    loyalty_min_redeem_cents: 500,
    loyalty_max_redeem_pct: 30,
  });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setForm(f => ({
      ...f,
      order_approval_mode: settings.order_approval_mode || "auto",
      order_approval_notify_emails: settings.order_approval_notify_emails || [],
      b2b_enabled: !!settings.b2b_enabled,
      b2b_price_display: settings.b2b_price_display || "TTC",
      b2b_allow_wire_transfer: settings.b2b_allow_wire_transfer !== false,
      b2b_wire_terms_days: settings.b2b_wire_terms_days ?? 30,
      b2b_default_discount_pct: settings.b2b_default_discount_pct ?? 0,
      b2b_pro_tiers: settings.b2b_pro_tiers || [],
      loyalty_enabled: !!settings.loyalty_enabled,
      loyalty_cashback_pct: settings.loyalty_cashback_pct ?? 5,
      loyalty_cashback_delay_days: settings.loyalty_cashback_delay_days ?? 15,
      loyalty_referral_bonus_cents: settings.loyalty_referral_bonus_cents ?? 1000,
      loyalty_min_redeem_cents: settings.loyalty_min_redeem_cents ?? 500,
      loyalty_max_redeem_pct: settings.loyalty_max_redeem_pct ?? 30,
    }));
    setDirty(false);
  }, [settings]);

  const patch = (k, v) => { setForm(f => ({ ...f, [k]: v })); setDirty(true); };

  const addTier = () => patch("b2b_pro_tiers", [...(form.b2b_pro_tiers || []), { id: `tier_${Date.now()}`, name: "Silver", discount_pct: 5, payment_terms_days: 30, min_order_cents: 0 }]);
  const patchTier = (i, field, val) => patch("b2b_pro_tiers", form.b2b_pro_tiers.map((t, idx) => idx === i ? { ...t, [field]: val } : t));
  const delTier = (i) => patch("b2b_pro_tiers", form.b2b_pro_tiers.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try { await api.put("/settings", form); await refresh(); setDirty(false); toast.success("Règles enregistrées"); }
    catch (e) { toast.error("Erreur : " + (e?.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const TITLES = {
    all: { label: "Règles métiers", title: "Approbation · Comptes PRO · Fidélité", desc: "Activez et configurez les mécaniques avancées de votre boutique." },
    approval: { label: "Règles métiers", title: "Approbation des commandes", desc: "Validez manuellement chaque commande avant préparation, ou laissez le flux automatique après paiement." },
    b2b: { label: "Règles métiers", title: "Comptes PRO / B2B", desc: "Comptes professionnels avec remises personnalisées, affichage HT/TTC, paliers Silver/Gold/VIP et paiement par virement." },
    loyalty: { label: "Règles métiers", title: "Fidélité & Wallet", desc: "Cashback sur chaque commande, bonus parrainage, wallet client utilisable au checkout." },
  };
  const meta = TITLES[section] || TITLES.all;
  const showApproval = section === "all" || section === "approval";
  const showB2B = section === "all" || section === "b2b";
  const showLoyalty = section === "all" || section === "loyalty";

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="business-rules-admin">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-[color:var(--brand-sky)] mb-1">
            <Briefcase className="w-3.5 h-3.5" /> {meta.label}
          </div>
          <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">{meta.title}</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-2xl">{meta.desc}</p>
        </div>
        <Button onClick={save} disabled={!dirty || saving} className="bg-[color:var(--brand-navy)]" data-testid="rules-save">
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
          {dirty ? "Enregistrer" : "Enregistré"}
        </Button>
      </header>

      {showApproval && (
      <Card className="p-5 space-y-3" data-testid="approval-section">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-[color:var(--brand-sky)]" />
          <h3 className="font-display text-lg font-bold text-[color:var(--brand-navy)]">Approbation des commandes</h3>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <label
            onClick={() => patch("order_approval_mode", "auto")}
            className={`p-4 rounded-xl border-2 cursor-pointer transition ${form.order_approval_mode === "auto" ? "border-[color:var(--brand-sky)] bg-sky-50/50" : "border-slate-200"}`}
            data-testid="approval-auto"
          >
            <div className="flex items-start gap-3">
              <div className={`w-5 h-5 rounded-full border-2 mt-0.5 shrink-0 ${form.order_approval_mode === "auto" ? "border-[color:var(--brand-sky)] bg-[color:var(--brand-sky)]" : "border-slate-300"}`}>
                {form.order_approval_mode === "auto" && <div className="w-full h-full rounded-full bg-white scale-[0.4]" />}
              </div>
              <div>
                <div className="text-sm font-bold text-[color:var(--brand-navy)]">Automatique (par défaut)</div>
                <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">Dès paiement confirmé → commande passe en « En préparation ». Aucune validation requise.</div>
              </div>
            </div>
          </label>
          <label
            onClick={() => patch("order_approval_mode", "manual")}
            className={`p-4 rounded-xl border-2 cursor-pointer transition ${form.order_approval_mode === "manual" ? "border-amber-500 bg-amber-50/50" : "border-slate-200"}`}
            data-testid="approval-manual"
          >
            <div className="flex items-start gap-3">
              <div className={`w-5 h-5 rounded-full border-2 mt-0.5 shrink-0 ${form.order_approval_mode === "manual" ? "border-amber-500 bg-amber-500" : "border-slate-300"}`}>
                {form.order_approval_mode === "manual" && <div className="w-full h-full rounded-full bg-white scale-[0.4]" />}
              </div>
              <div>
                <div className="text-sm font-bold text-[color:var(--brand-navy)]">Manuelle</div>
                <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">Les commandes payées arrivent en « En attente d'approbation ». Vous devez valider chaque commande dans l'onglet Commandes avant envoi en préparation.</div>
              </div>
            </div>
          </label>
        </div>
      </Card>
      )}

      {showB2B && (
      <Card className="p-5 space-y-4" data-testid="b2b-section">
        <label className="flex items-start gap-3 p-3 rounded-xl border-2 border-indigo-200 bg-indigo-50/30 cursor-pointer">
          <Switch checked={form.b2b_enabled} onCheckedChange={v => patch("b2b_enabled", v)} data-testid="b2b-toggle" />
          <div>
            <div className="font-bold text-[color:var(--brand-navy)] flex items-center gap-1"><Briefcase className="w-4 h-4" /> Comptes professionnels (B2B)</div>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5 max-w-xl">Débloque les comptes PRO avec remises personnalisées, affichage HT, paliers Silver/Gold/VIP et paiement par virement 30j.</p>
          </div>
        </label>
        {form.b2b_enabled && (
          <div className="space-y-3 pt-3 border-t">
            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs font-bold">Affichage des prix pour PRO</Label>
                <select value={form.b2b_price_display} onChange={e => patch("b2b_price_display", e.target.value)} className="mt-1.5 h-10 rounded-md border px-3 text-sm w-full bg-white" data-testid="b2b-price-display">
                  <option value="HT">HT (hors taxes)</option>
                  <option value="TTC">TTC (taxes incluses)</option>
                </select>
              </div>
              <div>
                <Label className="text-xs font-bold">Remise par défaut nouveau PRO (%)</Label>
                <Input type="number" step="0.5" min="0" max="50" value={form.b2b_default_discount_pct} onChange={e => patch("b2b_default_discount_pct", parseFloat(e.target.value) || 0)} className="mt-1.5" data-testid="b2b-default-discount" />
              </div>
              <label className="flex items-start gap-3 mt-5">
                <Switch checked={form.b2b_allow_wire_transfer} onCheckedChange={v => patch("b2b_allow_wire_transfer", v)} data-testid="b2b-wire-toggle" />
                <div>
                  <div className="text-sm font-bold text-[color:var(--brand-navy)]">Paiement par virement</div>
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))]">Autorise la facturation {form.b2b_wire_terms_days}j</div>
                </div>
              </label>
            </div>
            {/* Tiers PRO */}
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs font-bold flex items-center gap-1"><Gem className="w-3 h-3" /> Paliers PRO</Label>
                <Button size="sm" variant="outline" onClick={addTier} className="text-xs" data-testid="add-tier">+ Ajouter</Button>
              </div>
              <div className="mt-2 space-y-1.5">
                {(form.b2b_pro_tiers || []).map((t, i) => (
                  <div key={t.id || i} className="grid grid-cols-[1fr_100px_120px_auto] gap-2 items-center p-2 rounded-lg bg-slate-50" data-testid={`tier-${i}`}>
                    <Input value={t.name} onChange={e => patchTier(i, "name", e.target.value)} placeholder="Silver" className="h-8 text-sm" />
                    <div className="flex items-center gap-1">
                      <Input type="number" step="0.5" value={t.discount_pct || 0} onChange={e => patchTier(i, "discount_pct", parseFloat(e.target.value) || 0)} className="h-8 text-sm text-right tabular-nums" />
                      <span className="text-xs">%</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Input type="number" value={t.payment_terms_days || 0} onChange={e => patchTier(i, "payment_terms_days", parseInt(e.target.value) || 0)} className="h-8 text-sm text-right tabular-nums" />
                      <span className="text-xs">j</span>
                    </div>
                    <button onClick={() => delTier(i)} className="text-red-500 hover:text-red-700 text-xs p-1">✕</button>
                  </div>
                ))}
                {form.b2b_pro_tiers.length === 0 && <p className="text-[11px] italic text-[hsl(var(--muted-foreground))]">Aucun palier. Assignez une remise/conditions par tier à vos comptes PRO.</p>}
              </div>
            </div>
          </div>
        )}
      </Card>
      )}

      {showLoyalty && (
      <Card className="p-5 space-y-4" data-testid="loyalty-section">
        <label className="flex items-start gap-3 p-3 rounded-xl border-2 border-amber-200 bg-amber-50/30 cursor-pointer">
          <Switch checked={form.loyalty_enabled} onCheckedChange={v => patch("loyalty_enabled", v)} data-testid="loyalty-toggle" />
          <div>
            <div className="font-bold text-[color:var(--brand-navy)] flex items-center gap-1"><Award className="w-4 h-4 text-amber-500" /> Programme fidélité & Wallet</div>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5 max-w-xl">Chaque commande crédite un pourcentage sur le wallet du client. Parrainage : bonus parrain + filleul. Les clients peuvent utiliser leur wallet comme réduction au checkout.</p>
          </div>
        </label>
        {form.loyalty_enabled && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 pt-3 border-t">
            <div>
              <Label className="text-xs font-bold">Cashback (%)</Label>
              <Input type="number" step="0.5" min="0" max="50" value={form.loyalty_cashback_pct} onChange={e => patch("loyalty_cashback_pct", parseFloat(e.target.value) || 0)} className="mt-1.5" data-testid="loyalty-cashback" />
              <div className="text-[10px] text-slate-500 mt-1">% du total commande crédité sur le wallet</div>
            </div>
            <div>
              <Label className="text-xs font-bold">Délai de créditation (jours)</Label>
              <Input type="number" value={form.loyalty_cashback_delay_days} onChange={e => patch("loyalty_cashback_delay_days", parseInt(e.target.value) || 0)} className="mt-1.5" />
              <div className="text-[10px] text-slate-500 mt-1">Évite les annulations/retours avant crédit</div>
            </div>
            <div>
              <Label className="text-xs font-bold">Bonus parrainage (€)</Label>
              <Input type="number" step="0.5" value={(form.loyalty_referral_bonus_cents / 100).toFixed(2)} onChange={e => patch("loyalty_referral_bonus_cents", Math.round((parseFloat(e.target.value) || 0) * 100))} className="mt-1.5 tabular-nums" data-testid="loyalty-referral" />
              <div className="text-[10px] text-slate-500 mt-1">Offert au parrain et au filleul</div>
            </div>
            <div>
              <Label className="text-xs font-bold">Minimum pour utiliser le wallet (€)</Label>
              <Input type="number" step="0.5" value={(form.loyalty_min_redeem_cents / 100).toFixed(2)} onChange={e => patch("loyalty_min_redeem_cents", Math.round((parseFloat(e.target.value) || 0) * 100))} className="mt-1.5 tabular-nums" />
            </div>
            <div>
              <Label className="text-xs font-bold">Max du panier payable par wallet (%)</Label>
              <Input type="number" step="5" min="0" max="100" value={form.loyalty_max_redeem_pct} onChange={e => patch("loyalty_max_redeem_pct", parseFloat(e.target.value) || 0)} className="mt-1.5" />
            </div>
          </div>
        )}
      </Card>
      )}

      {dirty && (
        <div className="sticky bottom-4 flex justify-center z-30">
          <Card className="p-3 bg-[color:var(--brand-navy)] text-white border-0 shadow-2xl flex items-center gap-3">
            <span className="text-xs">Modifications non enregistrées</span>
            <Button size="sm" onClick={save} disabled={saving} className="bg-[color:var(--brand-sky)]">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
              Enregistrer
            </Button>
          </Card>
        </div>
      )}
    </div>
  );
}
